import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { NotebookPen, Loader2, Plus, ChevronRight, AlertCircle, Trash2, CalendarDays } from 'lucide-react';

const EMPTY = { title: '', session_date: '', notes: '' };

export const formatSessionDate = (d) => {
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const snippet = (t) => (t || '').replace(/\s+/g, ' ').trim().slice(0, 140);

function SessionRow({ s, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(s)} data-testid={`eng-session-row-${s.id}`}
      className="w-full flex items-center justify-between gap-3 p-3 bg-muted/30 hover:bg-muted/60 rounded-sm text-left transition-colors">
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate" data-testid={`eng-session-title-${s.id}`}>{s.title}</p>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
          <span data-testid={`eng-session-date-${s.id}`}>{formatSessionDate(s.session_date) || 'No date'}</span>
        </div>
        {s.notes && <p className="text-xs text-muted-foreground mt-1 truncate">{snippet(s.notes)}</p>}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

function SessionEditor({ editing, canEdit, saving, onChange, onSave, onRequestClose, onDelete }) {
  const isNew = !editing?.id;
  const f = editing?.form || EMPTY;
  return (
    <Dialog open={!!editing} onOpenChange={(v) => { if (!v) onRequestClose(); }}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="eng-session-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Session' : canEdit ? 'Edit Session' : 'Session'}</DialogTitle>
          <DialogDescription className="text-xs">Private to you. Never shared with clients.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="session-title" className="text-sm font-medium">Title</Label>
            <Input id="session-title" value={f.title} onChange={(e) => onChange({ title: e.target.value })} disabled={!canEdit}
              placeholder="e.g. Leadership workshop, day 1" className="rounded-sm" maxLength={200} data-testid="eng-session-title-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="session-date" className="text-sm font-medium">Date <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="session-date" type="date" value={f.session_date} onChange={(e) => onChange({ session_date: e.target.value })} disabled={!canEdit}
              className="rounded-sm w-full sm:w-52" data-testid="eng-session-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="session-notes" className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="session-notes" value={f.notes} onChange={(e) => onChange({ notes: e.target.value })} disabled={!canEdit}
              placeholder={canEdit ? "Whatever emerged — observations, themes, quotes, open questions…" : ""} rows={12}
              className="rounded-sm min-h-[240px] leading-relaxed" maxLength={50000} data-testid="eng-session-notes-input" />
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-between">
            {canEdit && !isNew ? (
              <Button type="button" variant="ghost" className="rounded-sm text-destructive hover:text-destructive" onClick={onDelete} data-testid="eng-session-delete-btn">
                <Trash2 className="h-4 w-4 mr-1.5" />Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" className="rounded-sm" onClick={onRequestClose} data-testid="eng-session-cancel-btn">{canEdit ? 'Cancel' : 'Close'}</Button>
              {canEdit && (
                <Button type="submit" disabled={saving} className="rounded-sm" data-testid="eng-session-save-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EngagementSessions({ engagementId, canEdit, onCountChange }) {
  const { get, post, put, del } = useApi();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [editing, setEditing] = useState(null); // { id?, form, initial }
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const base = `/engagements/${engagementId}/sessions`;
  const dirty = !!editing && JSON.stringify(editing.form) !== JSON.stringify(editing.initial);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await get(base);
      setItems(res || []); setStatus('ready'); onCountChange?.((res || []).length);
    } catch (e) { setStatus('error'); }
  }, [get, base]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const open = (s) => {
    const form = s ? { title: s.title || '', session_date: s.session_date || '', notes: s.notes || '' } : { ...EMPTY };
    setEditing({ id: s?.id, form, initial: form });
  };
  const change = (patch) => setEditing((e) => ({ ...e, form: { ...e.form, ...patch } }));
  const requestClose = () => {
    if (saving) return;
    if (dirty && !window.confirm('Discard unsaved changes to this session?')) return;
    setEditing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    const { title, session_date, notes } = editing.form;
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    const body = { title: title.trim(), session_date: session_date || null, notes: notes || null };
    setSaving(true);
    try {
      if (editing.id) await put(`${base}/${editing.id}`, body);
      else await post(base, body);
      toast.success('Session saved');
      setEditing(null);
      load();
    } catch (err) { toast.error(err.message || 'Could not save session'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await del(`${base}/${editing.id}?confirm=true`);
      toast.success('Session deleted');
      setConfirmDelete(false); setEditing(null); load();
    } catch (err) { toast.error(err.message || 'Could not delete session'); }
  };

  return (
    <Card className="rounded-sm border" data-testid="eng-sessions">
      <CardHeader className="pb-4 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base font-medium">Sessions ({items.length})</CardTitle>
        {canEdit && (
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => open(null)} data-testid="eng-new-session-btn">
            <Plus className="h-4 w-4 mr-1.5" />New Session
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {status === 'loading' && <div className="flex justify-center py-8" data-testid="eng-sessions-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {status === 'error' && (
          <div className="text-center py-8" data-testid="eng-sessions-error">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
            <p className="text-sm text-muted-foreground mb-3">Couldn't load sessions</p>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={load} data-testid="eng-sessions-retry">Try again</Button>
          </div>
        )}
        {status === 'ready' && items.length === 0 && (
          <div className="text-center py-8" data-testid="eng-sessions-empty">
            <NotebookPen className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No sessions recorded yet</p>
            {canEdit && <p className="text-xs text-muted-foreground mt-1">Capture what emerged from each meeting, workshop or conversation</p>}
          </div>
        )}
        {status === 'ready' && items.length > 0 && (
          <div className="space-y-2">{items.map((s) => <SessionRow key={s.id} s={s} onOpen={open} />)}</div>
        )}
      </CardContent>

      <SessionEditor editing={editing} canEdit={canEdit} saving={saving} onChange={change}
        onSave={save} onRequestClose={requestClose} onDelete={() => setConfirmDelete(true)} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="eng-session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>The session and its notes will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="eng-session-delete-cancel">Keep session</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={remove} data-testid="eng-session-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
